import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  Optional,
  ViewEncapsulation,
  forwardRef,
} from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatCalendar, MatCalendarHeader, MatDatepickerIntl } from "@angular/material/datepicker";
import { Translator } from "../../services/translator/translator.service";
import {
  attachMatDatepickerContentResizeReposition,
  scheduleMatDatepickerPopupReposition,
} from "../../utils/mat-datepicker-popup-reposition.util";

@Component({
  selector: "app-limited-years-calendar-header",
  templateUrl: "./limited-years-calendar-header.component.html",
  styleUrls: ["./limited-years-calendar-header.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LimitedYearsCalendarHeaderComponent
  extends MatCalendarHeader<any>
  implements AfterViewInit, OnDestroy
{
  private detachResizeReposition?: () => void;
  private static readonly _YEARS_PER_PAGE = 24;

  showPeriodNavArrows(): boolean {
    if (this.calendar.currentView !== "multi-year") {
      return true;
    }
    const min = this.calendar.minDate;
    const max = this.calendar.maxDate;
    if (!min || !max) {
      return true;
    }
    const minY = this.dateAdapter.getYear(min);
    const maxY = this.dateAdapter.getYear(max);
    const span = maxY - minY + 1;
    return span > LimitedYearsCalendarHeaderComponent._YEARS_PER_PAGE;
  }

  constructor(
    private intl: MatDatepickerIntl,
    @Inject(forwardRef(() => MatCalendar)) calendar: MatCalendar<any>,
    private dateAdapter: DateAdapter<any>,
    @Optional() @Inject(MAT_DATE_FORMATS) private dateFormats: any,
    private cdr: ChangeDetectorRef,
    private translator: Translator,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {
    super(intl, calendar, dateAdapter, dateFormats, cdr);
  }

  ngAfterViewInit(): void {
    this.detachResizeReposition = attachMatDatepickerContentResizeReposition(
      this.elementRef,
      this.ngZone
    );
  }

  ngOnDestroy(): void {
    this.detachResizeReposition?.();
  }

  setView(view: "month" | "year" | "multi-year"): void {
    this.calendar.currentView = view;
    this.cdr.markForCheck();
    scheduleMatDatepickerPopupReposition(this.ngZone);
  }

  navLabel(key: string, fallback: string): string {
    const v = this.translator.instant(key);
    return !v || v === key ? fallback : v;
  }

  get periodButtonText(): string {
    const view = this.calendar.currentView;
    if (view === "month" && this.dateFormats) {
      return this.dateAdapter
        .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
        .toLocaleUpperCase();
    }
    if (view === "year") {
      return this.dateAdapter.getYearName(this.calendar.activeDate);
    }
    if (view === "multi-year") {
      /** Sempre o intervalo da página atual (~24 anos), não o span total min–max do calendário. */
      return this.multiYearPeriodLabelFallback();
    }
    return "";
  }

  private multiYearPeriodLabelFallback(): string {
    const a = this.dateAdapter;
    const activeYear = a.getYear(this.calendar.activeDate);
    const startingYear = LimitedYearsCalendarHeaderComponent.startingYearForPage(
      a,
      this.calendar.minDate,
      this.calendar.maxDate
    );
    const ypp = LimitedYearsCalendarHeaderComponent._YEARS_PER_PAGE;
    const offset = ((activeYear - startingYear) % ypp + ypp) % ypp;
    const minYearOfPage = activeYear - offset;
    const maxYearOfPage = minYearOfPage + LimitedYearsCalendarHeaderComponent._YEARS_PER_PAGE - 1;
    const minName = a.getYearName(a.createDate(minYearOfPage, 0, 1));
    const maxName = a.getYearName(a.createDate(maxYearOfPage, 0, 1));
    return this.intl.formatYearRange(minName, maxName);
  }

  private static startingYearForPage(
    dateAdapter: DateAdapter<any>,
    minDate: any,
    maxDate: any
  ): number {
    if (maxDate) {
      return dateAdapter.getYear(maxDate) - LimitedYearsCalendarHeaderComponent._YEARS_PER_PAGE + 1;
    }
    if (minDate) {
      return dateAdapter.getYear(minDate);
    }
    return 0;
  }

  previousClicked(): void {
    if (this.calendar.currentView === "multi-year") {
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, -1);
      return;
    }
    super.previousClicked();
  }

  nextClicked(): void {
    if (this.calendar.currentView === "multi-year") {
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, 1);
      return;
    }
    super.nextClicked();
  }

  previousEnabled(): boolean {
    if (this.calendar.currentView === "multi-year" && this.calendar.minDate) {
      const minY = this.dateAdapter.getYear(this.calendar.minDate);
      const activeY = this.dateAdapter.getYear(this.calendar.activeDate);
      return activeY > minY;
    }
    return super.previousEnabled();
  }

  nextEnabled(): boolean {
    if (this.calendar.currentView === "multi-year" && this.calendar.maxDate) {
      const maxY = this.dateAdapter.getYear(this.calendar.maxDate);
      const activeY = this.dateAdapter.getYear(this.calendar.activeDate);
      return activeY < maxY;
    }
    return super.nextEnabled();
  }
}
